import { useTranslation } from 'react-i18next'
import { Facebook, Instagram, MousePointerClick, Reply, Target, TrendingUp } from 'lucide-react'
import { ReportChart } from '@/components/relatorios/ReportChart'
import { ReportKpiCard } from '@/components/relatorios/ReportKpiCard'
import { ReportShell } from '@/components/relatorios/ReportShell'
import { ReportTable } from '@/components/relatorios/ReportTable'
import { exportCsv, formatDuration, formatPercent } from '@/components/relatorios/reportUtils'
import { useMarketingPerformanceReport } from '@/hooks/reports/reportHooks'
import { useReportDateRange } from '@/hooks/reports/useReportDateRange'
import { useCurrency } from '@/hooks/useCurrency'
import { formatDayKey } from '@/lib/accountTimezone'
import { formatNumber } from '@/lib/dashboardFormat'

const funnelPalette = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)']

const heroAccents = [
  { bg: 'bg-blue-50 dark:bg-blue-500/15', fg: 'text-[#2563EB]', value: 'text-[#2563EB]', Icon: MousePointerClick },
  { bg: 'bg-green-50 dark:bg-green-500/15', fg: 'text-[#22C55E]', value: 'text-[#22C55E]', Icon: Reply },
  { bg: 'bg-purple-50 dark:bg-purple-500/15', fg: 'text-[#9333EA]', value: 'text-[#9333EA]', Icon: Target },
  { bg: 'bg-orange-50 dark:bg-orange-500/15', fg: 'text-[#F97316]', value: 'text-[#F97316]', Icon: TrendingUp },
] as const

function HeroStat({
  index,
  label,
  value,
  helper,
}: {
  index: number
  label: string
  value: string
  helper?: string
}) {
  const accent = heroAccents[index % heroAccents.length]
  const Icon = accent.Icon
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent.bg} ${accent.fg}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className={`text-2xl font-semibold ${accent.value}`}>{value}</p>
        </div>
      </div>
      {helper && <p className="mt-3 text-xs text-muted-foreground">{helper}</p>}
    </div>
  )
}

export default function PerformanceMarketingReport() {
  const { t } = useTranslation('reports')
  const { formatCurrency } = useCurrency()
  const [range, setRange] = useReportDateRange()
  const report = useMarketingPerformanceReport(range)
  const { totals, funnel, daily, byPlatform, creatives, formBreakdown } = report.data

  const funnelSteps = [
    { label: t('marketing.heroLeads'), value: funnel.leads },
    { label: t('fields.responded'), value: funnel.responded },
    { label: t('fields.opportunities'), value: funnel.opportunities },
    { label: t('fields.sales'), value: funnel.sales },
  ]
  const funnelMax = funnelSteps[0].value || 1

  return (
    <ReportShell
      title={t('marketing.title')}
      description={t('marketing.description')}
      {...range}
      onRangeChange={setRange}
      loading={report.loading}
      error={report.error}
      empty={totals.leads === 0}
      onRetry={report.refetch}
      onExport={() =>
        exportCsv('performance-marketing', creatives, [
          { label: t('fields.creative'), value: (row) => row.title },
          { label: t('fields.adId'), value: (row) => (row.attributionKey.includes(':') ? row.attributionKey.split(':')[1] : t('fields.noAdId')) },
          { label: t('fields.platform'), value: (row) => row.sourceApp },
          { label: t('fields.leads'), value: (row) => row.leads },
          { label: t('fields.responded'), value: (row) => row.responded },
          { label: t('fields.opportunities'), value: (row) => row.opportunities },
          { label: t('fields.sales'), value: (row) => row.sales },
          { label: t('fields.revenue'), value: (row) => row.revenue.toFixed(2).replace('.', ',') },
          { label: t('fields.conversionPct'), value: (row) => row.conversion.toFixed(1).replace('.', ',') },
          { label: t('fields.avgTicket'), value: (row) => row.averageTicket.toFixed(2).replace('.', ',') },
        ])
      }
    >
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <HeroStat index={0} label={t('marketing.heroLeads')} value={formatNumber(totals.leads)} helper={t('marketing.heroLeadsHelper')} />
        <HeroStat index={1} label={t('marketing.heroRevenue')} value={formatCurrency(totals.revenue)} helper={t('marketing.heroRevenueHelper', { count: totals.sales })} />
        <HeroStat index={2} label={t('marketing.heroConversion')} value={formatPercent(totals.leadToSale)} helper={t('marketing.heroConversionHelper', { count: totals.opportunities })} />
        <HeroStat index={3} label={t('marketing.heroFirstResponse')} value={formatDuration(totals.averageFirstResponseSeconds)} helper={t('marketing.heroFirstResponseHelper', { count: totals.responded })} />
      </section>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ReportKpiCard label={t('fields.opportunities')} value={formatNumber(totals.opportunities)} />
        <ReportKpiCard label={t('fields.sales')} value={formatNumber(totals.sales)} />
        <ReportKpiCard label={t('marketing.kpiFacebook')} value={formatNumber(totals.facebook)} icon={<Facebook className="h-4 w-4" />} />
        <ReportKpiCard label={t('marketing.kpiInstagram')} value={formatNumber(totals.instagram)} icon={<Instagram className="h-4 w-4" />} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">{t('marketing.funnelTitle')}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{t('marketing.funnelSubtitle')}</p>
          <div className="mt-4 flex flex-col gap-3">
            {funnelSteps.map((step, index) => {
              const width = Math.max((step.value / funnelMax) * 100, step.value > 0 ? 6 : 2)
              const previous = index === 0 ? step.value : funnelSteps[index - 1].value
              const stepRate = previous ? (step.value / previous) * 100 : 0
              return (
                <div key={step.label}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground">{step.label}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {formatNumber(step.value)}
                      {index > 0 && <span className="ml-2 text-xs">({formatPercent(stepRate)})</span>}
                    </span>
                  </div>
                  <div className="mt-1 h-3 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${width}%`, backgroundColor: funnelPalette[index % funnelPalette.length] }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <ReportChart
          type="line"
          data={daily.map((row) => ({ ...row, label: formatDayKey(row.date, { day: '2-digit', month: '2-digit' }) }))}
          xKey="label"
          series={[
            { key: 'leads', label: t('fields.leads'), color: 'var(--chart-1)' },
            { key: 'sales', label: t('fields.sales'), color: 'var(--chart-2)' },
          ]}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('marketing.platformPerformance')}</h2>
          <ReportChart
            type="bar"
            data={byPlatform}
            xKey="label"
            series={[
              { key: 'leads', label: t('fields.leads'), color: 'var(--chart-1)' },
              { key: 'sales', label: t('fields.sales'), color: 'var(--chart-2)' },
            ]}
          />
        </div>
        <div>
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('marketing.platforms')}</h2>
          <ReportTable
            rows={byPlatform}
            rowKey={(row) => row.platform || 'none'}
            columns={[
              { key: 'label', label: t('fields.platform'), render: (row) => <span className="font-medium text-foreground">{row.label}</span> },
              { key: 'leads', label: t('fields.leads'), align: 'right', render: (row) => row.leads },
              { key: 'sales', label: t('fields.sales'), align: 'right', render: (row) => row.sales },
              { key: 'revenue', label: t('fields.revenue'), align: 'right', render: (row) => formatCurrency(row.revenue) },
              { key: 'conversion', label: t('fields.conversionShort'), align: 'right', render: (row) => formatPercent(row.conversion) },
            ]}
          />
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('marketing.creativeRanking')}</h2>
        <ReportTable
          rows={creatives}
          rowKey={(row) => row.attributionKey}
          columns={[
            { key: 'title', label: t('fields.creative'), render: (row) => <span className="font-medium text-foreground">{row.title}</span> },
            {
              key: 'adId',
              label: t('fields.adId'),
              render: (row) => {
                const id = row.attributionKey.includes(':')
                  ? row.attributionKey.split(':')[1]
                  : null
                return id ? <span className="font-mono text-xs text-muted-foreground">{id}</span> : <span className="text-xs text-muted-foreground">{t('fields.noAdId')}</span>
              },
            },
            { key: 'sourceApp', label: t('fields.platform'), render: (row) => <span className="capitalize">{row.sourceApp}</span> },
            { key: 'leads', label: t('fields.leads'), align: 'right', render: (row) => row.leads },
            { key: 'responded', label: t('fields.responded'), align: 'right', render: (row) => row.responded },
            { key: 'opportunities', label: t('fields.opportunitiesShort'), align: 'right', render: (row) => row.opportunities },
            { key: 'sales', label: t('fields.sales'), align: 'right', render: (row) => row.sales },
            { key: 'revenue', label: t('fields.revenue'), align: 'right', render: (row) => formatCurrency(row.revenue) },
            { key: 'conversion', label: t('fields.conversion'), align: 'right', render: (row) => formatPercent(row.conversion) },
            { key: 'averageTicket', label: t('fields.avgTicket'), align: 'right', render: (row) => formatCurrency(row.averageTicket) },
          ]}
        />
      </div>

      {formBreakdown.length > 0 && (
        <div>
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('marketing.formAnswers')}</h2>
          <p className="mb-3 text-xs text-muted-foreground">{t('marketing.formAnswersHint')}</p>
          <ReportTable
            rows={formBreakdown}
            rowKey={(row, index) => `${row.question}-${row.answer}-${index}`}
            columns={[
              { key: 'question', label: t('fields.question'), render: (row) => row.question },
              { key: 'answer', label: t('fields.answer'), render: (row) => <span className="font-medium text-foreground">{row.answer}</span> },
              { key: 'leads', label: t('fields.leads'), align: 'right', render: (row) => row.leads },
              { key: 'sales', label: t('fields.sales'), align: 'right', render: (row) => row.sales },
              { key: 'revenue', label: t('fields.revenue'), align: 'right', render: (row) => formatCurrency(row.revenue) },
              { key: 'conversion', label: t('fields.conversion'), align: 'right', render: (row) => formatPercent(row.conversion) },
            ]}
          />
        </div>
      )}
    </ReportShell>
  )
}
